import { appSync } from '../app/app_sync.js';
import { navigation } from '../app/navigation.js';
import { assert, error, log } from '../app/logger.js';
import { friends } from '../data/friends.js';
import { Trip } from '../data/trip.js';
import { trips } from '../data/trips.js';
import { PanelBase } from '../ui/panel_base.js';
import { UIQueueable } from '../ui/ui_queueable.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { storage } from '../app/storage.js';
import { AcceptTripShareDialog } from './accept_trip_share_dialog.js';
import { CreateTripDialog } from './create_trip_dialog.js';
import { RejectTripShareDialog } from './reject_trip_share_dialog.js';
import { RemoveTripDialog } from './remove_trip_dialog.js';

import '../components/goingwith_control.js';
import './trip_badge.js';

/**
 * Trip planning panel.
 */
class TripPlanningPanel extends PanelBase {
  static id = navigation.kTripPlanningPanelId;

  static get selectors() {
    const PlanningBadge = '#planning-li';
    const LoadingBadge = `${PlanningBadge} + .trips-loading-box`;
    const AllPlannedTrips = `${LoadingBadge} ~ trip-badge`;
    const AllPastTripGroups = '.past-trip-group';
    const PastTripGroupBy = year => `.trips-${year}`;
    return Object.assign({}, super.selectors, {
      AllPlannedTrips,
      PlannedTripAt(i) {
        return `${AllPlannedTrips}:nth-of-type(${i + 1})`;
      },
      PlanningBadge,
      PlanningLink: `${PlanningBadge} .link`,
      MainList: '#list',
      CurrentTripBadge: 'trip-badge[current]',
      PastTripContainer: '#past-trip-container',
      AllPastTripGroups,
      AllPastTripGroupTitles: `${AllPastTripGroups} .list-heading-text`,
      PastTripGroupAt(i) {
        return `${AllPastTripGroups}:nth-of-type(${i + 1}`;
      },
      PastTripGroupBy,
      PastTripListBy(year) {
        return `${PastTripGroupBy(year)} .list`;
      },
      PastTripAt(year, i) {
        return `${this.PastTripListBy(year)} > trip-badge:nth-of-type(${
          i + 1
        })`;
      },
    });
  }

  static get elements() {
    return {
      currentTripBadge: this.selectors.CurrentTripBadge,
      mainListElement: this.selectors.MainList,
      pastTripContainerElement: this.selectors.PastTripContainer,
      planningLink: this.selectors.PlanningLink,
      plannedTripBadgeAt: i => this.selectors.PlannedTripAt(i),
      pastTripGroupAt: i => this.selectors.PastTripGroupAt(i),
      pastTripGroupBy: year => this.selectors.PastTripGroupBy(year),
      pastTripListBy: year => this.selectors.PastTripListBy(year),
      pastTripBadgeAt: (year, i) => this.selectors.PastTripAt(year, i),
    };
  }

  get historyState() {
    return [
      { page: this.page },
      `CamperAid - Trip Planning`,
      window.location.origin,
    ];
  }

  // Loads trips and selects a given trip if any.
  async loadTrips({ id } = {}) {
    log(`[trip-planning] load trips`);

    this.loadingOn();

    // Load trips.
    log(
      `[trip-planning] planned trips: ${trips.plannedTrips
        .map(t => t.stringify())
        .join(', ')}`
    );
    log(
      `[trip-planning] past trips: ${trips.pastTripYears
        .map(
          year =>
            `${year}: ${trips
              .getPastTrips(year)
              .map(t => t.stringify())
              .join(', ')}`
        )
        .join(', ')}`
    );

    if (!this.#plannedTripsUnpipeable) {
      this.#plannedTripsUnpipeable = trips.pipeables.plannedTrips.pipe(
        (array, diff) =>
          this.#syncTripBadges(
            this.mainListElement,
            this.plannedTripBadgeAt.bind(this),
            diff
          )
      );
    }

    if (!this.#pastTripYearsUnpipeable) {
      this.#pastTripYearsUnpipeable = trips.pipeables.pastTripYears.pipe(
        (...args) => this.#syncPastTripYears(...args)
      );
    }

    if (!this.#hasTripsUnpipeable) {
      this.#hasTripsUnpipeable = trips.pipeables.hasTrips.pipe(hasTrips =>
        hasTrips
          ? this.setAttribute('has-trips', 'true')
          : this.removeAttribute('has-trips')
      );
    }

    this.loadingOff();

    // Switch to.
    await trips.loadIfNeeded();

    let tripIdentifier = (id && { id }) || this.#currentTrip;
    return tripIdentifier && this.switchTo(tripIdentifier);
  }

  switchTo(tripIdentifier) {
    if (navigation.trip.isOpenFor(tripIdentifier)) {
      return;
    }

    const { id } = tripIdentifier;
    log(`[trip-planning] switch to ${id} trip`);
    let trip =
      tripIdentifier instanceof Trip ? tripIdentifier : trips.findTrip({ id });
    return this.#setCurrent(trip);
  }

  planTrip(loc) {
    return CreateTripDialog.get({
      onaccept: async options => {
        await window.map_setLocationIfNeeded(options.location);
        return this.createTrip(options);
      },
    }).show(loc);
  }

  async createTrip(options) {
    assert(options, `[trip-planning] createTrip: no options`);
    log(`[trip-planning] create new trip to ${options.location.name}`);

    // Remove maps from histroy stack and switch to the trip panel.
    const navigationPromise = navigation.closeMap().then(() =>
      navigation.toTrip({
        loading: true,
      })
    );

    let trip = await trips.createTrip(options);
    if (!trip) {
      error(`[trip-planning] failed to create a new trip`);
      return null;
    }
    await this.#setCurrent(trip);
    log(`[trip-planning] new trip '${trip.id}' created`);

    return navigationPromise;
  }

  async cloneTrip(trip) {
    assert(trip, `[trip-planning] cloneTrip: no trip`);
    log(`[trip-planning] clone ${trip.id}`);

    // Switch to trip panel.
    const navigationPromise = navigation.toTrip({
      loading: true,
    });

    let cloneTrip = await trips.cloneTrip(trip);
    if (!cloneTrip) {
      error(`[trip-planning] failed to clone list ${trip.id}`);
      return null;
    }

    await this.#setCurrent(cloneTrip);
    log(
      `[trip-planning] list ${trip.id} was cloned, cloned trip id: ${cloneTrip.id}`
    );

    navigation.trip.selectTitle();
    return navigationPromise;
  }

  removeTrip(trip) {
    assert(trip, `[trip-planning] removeTrip: no trip`);
    return RemoveTripDialog.get({
      onaccept: UIQueueable(trip =>
        this.#removeOrRejectTrip(trip, () => trips.removeTrip(trip))
      ),
    }).show(trip);
  }

  async acceptShare(hash) {
    await trips.load();
    const shareInfo = await trips.getShareInfo(hash);

    return AcceptTripShareDialog.get({
      onaccept: async hash => {
        log(`[trip-planning] accept ${hash} invite`);
        let tripIdentifier = await trips.acceptShare(hash);
        if (!tripIdentifier) {
          return;
        }

        await Promise.all([friends.load(), trips.load()]);
        await this.loadTrips(tripIdentifier);

        appSync.syncTrip.notifyCocampersChanged();
        log(`[trip-planning] ${hash} invite accepted`);
      },
    }).show(hash, shareInfo);
  }

  rejectShare(trip) {
    assert(trip, `[trip-planning] rejectShare: no trip`);
    return RejectTripShareDialog.get({
      onaccept: UIQueueable(trip =>
        this.#removeOrRejectTrip(trip, () => trips.rejectSharedTrip(trip))
      ),
    }).show(trip);
  }

  syncChecklist(tripIdentifier) {
    log(`[trip-planning] sync checklist for ${tripIdentifier.id} trip`);
    let trip = trips.findTrip(tripIdentifier);
    if (trip) {
      trip.entities.checklist.invalidate();
      navigation.trip.syncChecklist(trip);
    }
  }

  syncFoodMenu(tripIdentifier) {
    log(`[trip-planning] sync food menu for ${tripIdentifier.id} trip`);
    let trip = trips.findTrip(tripIdentifier);
    if (trip) {
      trip.entities.menu.invalidate();
      navigation.trip.syncFoodMenu(trip);
    }
  }

  // Reload invites info.
  async syncCocampers(tripIdentifier) {
    log(`[trip-planning] sync cocampers for ${tripIdentifier.id} trip`);

    await friends.load();
    await trips.reloadCocampers(tripIdentifier);

    // TODO: need to update relevant trip badges.
    let trip = this.#currentTrip;
    if (trip.is(tripIdentifier)) {
      await navigation.trip.setOptions(trip);
    }
  }

  initialize() {
    super.initialize();

    this.createFragmentProperty('list');
    listenToEvent(this.planningLink, 'click', () => navigation.exploreMap());
  }

  static markup = `
    <div class='list-heading no-trips-no-plans'>
      <span class='list-heading-text'>Trips planning</span>
    </div>

    <div class='spacer no-trips-no-plans'></div>
    <ul id='list' class='list no-trips-no-plans'>
      <li id='planning-li' class='trip'>
        <div>
          <span class='link'>Plan </span>
          <span>a trip.</span>
        </div>
      </li>
      <li class='trip trips-loading-box'>
        <div class='trip-body'>
          <img class='trip-ico' src='/images/picture.png'>
          <div class='trip-data'>
            <div class='loading-bar' style='width: 60%'></div>
            <div class='loading-bar' style='width: 40%'></div>
          </div>
        </div>
        <div class='trip-title'>
          <div class='trip-name'>
            <div class='loading-bar' style='width: 80%'></div>
          </div>
          <div class='loading-bar' style='width: 60%'></div>
        </div>
      </li>
    </ul>
    <div class='spacer-2 no-trips-no-plans'></div>

    <!-- Past trips -->
    <details id='past-trip-container' hidden>
      <summary class='list-heading'>
        <span class='list-heading-text'>Past trips</span>
      </summary>
    </details>
  `;

  static listMarkup = `
    <details class='past-trip-group'>
      <summary class='list-heading'>
        <span class='list-heading-text'></span>
      </summary>
      <ul class='list'></ul>
    </details>
  `;

  #removeOrRejectTrip(trip, dataFunction) {
    assert(trip, `[trip-planning] #removeOrRejectTrip: no trip`);
    let badgeEl = this.#getTripBadge(trip);
    badgeEl.setAttribute('vanishing', 'true');

    return dataFunction().then(() => this.#removeTripBadge(badgeEl));
  }

  #syncTripBadges(listEl, getTripBadge, diff) {
    log(
      `[trip-planning] #syncTripBadges for ${diff
        .map(e => {
          let trip = e.is || e.was;
          return `${trip.isPlanned ? 'planned' : trip.year}: ${e.what} at ${
            e.idx
          } ${trip}}`;
        })
        .join(', ')}`
    );
    for (let diffEntry of diff) {
      switch (diffEntry.what) {
        case 'changed':
          {
            let badgeEl = getTripBadge(diffEntry.idx);
            assert(
              badgeEl,
              `[trip-planning] no trip badge at ${diffEntry.idx}`
            );
            badgeEl?.set(diffEntry.is);
          }
          break;

        case 'deleted':
          {
            let badgeEl = getTripBadge(diffEntry.idx);
            assert(
              badgeEl,
              `[trip-planning] no trip badge at ${diffEntry.idx}`
            );
            badgeEl?.remove();
          }
          break;

        case 'inserted':
          {
            let badgeEl = getTripBadge(diffEntry.idx);
            listEl.insertBefore(this.#createTripBadge(diffEntry.is), badgeEl);
          }
          break;
      }
    }
  }

  #syncPastTripYears(years, diff) {
    log(
      `[trip-planning] #syncPastTripYears for ${diff
        .map(e => `${e.what} at ${e.idx} ${e.is || e.was}}`)
        .join(', ')}`
    );
    for (let diffEntry of diff) {
      switch (diffEntry.what) {
        case 'changed':
          this.#removeTripGroup(diffEntry.idx);
          this.#insertTripGroup(diffEntry.idx, diffEntry.is);
          break;

        case 'deleted':
          this.#removeTripGroup(diffEntry.idx);
          break;

        case 'inserted':
          this.#insertTripGroup(diffEntry.idx, diffEntry.is);
          break;
      }
    }
    this.pastTripContainerElement.hidden = years.length == 0;
  }

  #insertTripGroup(index, year) {
    assert(index !== undefined, `[trip-planning] #insertTripGroup: no index`);
    assert(year, `[trip-planning] #insertTripGroup: no year`);

    let listContainerEl = this.listFragment;
    listContainerEl.querySelector('.list-heading-text').textContent = year;

    let detailsEl = listContainerEl.querySelector('details');
    detailsEl.classList.add(`trips-${year}`);
    detailsEl.addEventListener('toggle', () => {
      log(
        `[trip-planning] ${
          detailsEl.open ? 'open' : 'close'
        } ${year} past trip group`
      );
      if (detailsEl.open && !detailsEl.pipe) {
        detailsEl.pipe = trips.pipeables
          .getPastTrips(year)
          .pipe((trips, diff) =>
            this.#syncTripBadges(
              this.pastTripListBy(year),
              this.pastTripBadgeAt.bind(this, year),
              diff
            )
          );
      }
    });

    this.pastTripContainerElement.insertBefore(
      listContainerEl,
      this.pastTripGroupAt(index)
    );
  }

  #removeTripGroup(index) {
    let listEl = this.pastTripGroupAt(index);
    assert(listEl, `[trip-planning] no trip list at ${index}`);
    if (listEl && listEl.pipe) {
      listEl.pipe.unpipe();
      listEl.remove();
    }
  }

  #createTripBadge(trip) {
    assert(trip, `[trip-planning] #createTripBadge: no trip`);
    let badgeEl = window.document.createElement('trip-badge');
    badgeEl.setAttribute('role', 'listitem');
    badgeEl.friends = friends;
    badgeEl.set(trip);
    return badgeEl;
  }

  #removeTripBadge(badgeEl) {
    assert(badgeEl, `[trip-planning] #removeTripBadge: no badgeEl`);
    navigation.trip.clear();

    let siblingBadgeEl = null;
    if (badgeEl.nextElementSibling) {
      siblingBadgeEl = badgeEl.nextElementSibling;
    } else if (
      badgeEl.previousElementSibling &&
      !this.#isFakeTripBadge(badgeEl.previousElementSibling)
    ) {
      siblingBadgeEl = badgeEl.previousElementSibling;
    }
    badgeEl.remove();

    let siblingTripId = (siblingBadgeEl && siblingBadgeEl.tripId) || 0;
    return siblingTripId && this.switchTo({ id: siblingTripId });
  }

  get #currentTrip() {
    let tripIdentifier = this.#currentTripIdentifier;
    return (
      (tripIdentifier && trips.findTrip(tripIdentifier)) || trips.firstTrip
    );
  }

  async #setCurrent(trip) {
    assert(trip, `[trip-planning] #setCurrent: no trip`);
    let prevTripBadge = this.currentTripBadge;
    if (prevTripBadge) {
      prevTripBadge.current = false;
    }

    if (!trip.isPlanned) {
      let detailsEl = this.pastTripGroupBy(trip.year);
      if (!detailsEl.open) {
        let waitUntilOpen = new Promise(resolve =>
          detailsEl.addEventListener('toggle', resolve, { once: true })
        );
        detailsEl.open = true;
        await waitUntilOpen;
      }
      this.pastTripContainerElement.open = true;
    }

    let badge = this.#getTripBadge(trip);
    assert(badge, `no badge for ${trip} trip`);
    badge.current = true;

    this.#currentTripIdentifier = trip;
    appSync.syncTrip.connect(trip.id);
    return navigation.trip.set(trip);
  }

  #getTripBadge({ id }) {
    let selector = `[trip-id='${id}']`;
    let badgeEl = this.querySelector(selector);
    assert(badgeEl, `[trip-planning] no badge element for ${selector}`);
    return badgeEl;
  }

  #isFakeTripBadge(el) {
    return el.localName != 'trip-badge';
  }

  get #currentTripIdentifier() {
    let id = parseInt(storage.get('current-trip-id'));
    if (isNaN(id)) {
      return null;
    }
    return { id };
  }

  set #currentTripIdentifier(trip) {
    storage.set('current-trip-id', trip.id);
  }

  #hasTripsUnpipeable;
  #pastTripYearsUnpipeable;
  #plannedTripsUnpipeable;
}

navigation.registerPanel(TripPlanningPanel);

export { TripPlanningPanel };
